/**
 * Analytics Service - PostHog Integration for Webapp
 *
 * Central service for all analytics tracking. Wraps PostHog browser SDK
 * and provides type-safe event tracking using generators.
 *
 * The webapp uses posthog-js/react with a provider, so this service
 * captures the PostHog instance after initialization.
 *
 * Usage:
 * ```typescript
 * import { analytics, CustomerEvents } from '@/services/analytics';
 *
 * // Track events using generators (preferred - type-safe)
 * analytics.trackEvent(CustomerEvents.created({ source: 'manual' }));
 *
 * // Track a page view
 * analytics.pageView('Customers', '/customers');
 * ```
 */

import type { PostHog } from 'posthog-js';
import type { GeneratedEvent } from './generators';
import type { AnalyticsEventName, UserTraits } from './events';

export const appSuperProperties = {
  app_source: 'webapp',
  environment: import.meta.env.VITE_ENVIRONMENT || 'development',
};

class AnalyticsService {
  private posthog: PostHog | null = null;
  private pendingEvents: Array<{
    event: string;
    properties?: Record<string, unknown>;
  }> = [];

  /**
   * Set the PostHog instance. Called by usePostHogIdentify hook after init.
   */
  setPostHog(instance: PostHog | null): void {
    this.posthog = instance;
    if (instance) {
      this.flushPendingEvents();
    }
  }

  /**
   * Track an event using a generator (preferred - type-safe)
   *
   * @example
   * analytics.trackEvent(CustomerEvents.created({ source: 'manual' }));
   * analytics.trackEvent(OnboardingEvents.stepCompleted({ step: 'organization' }));
   */
  trackEvent(generated: GeneratedEvent): void {
    this.track(generated.event, generated.properties);
  }

  /**
   * Track a raw event (escape hatch for edge cases)
   * Prefer trackEvent() with generators for type safety.
   */
  track(
    event: AnalyticsEventName | string,
    properties?: Record<string, unknown>
  ): void {
    const enriched = this.withScorecardAliases(properties);

    if (!this.posthog) {
      // Queue event if PostHog not ready yet
      this.pendingEvents.push({ event, properties: enriched });
      console.log('[Analytics] Event queued (not ready):', event);
      return;
    }

    try {
      this.posthog.capture(event, enriched);
      console.log('[Analytics] Event tracked:', event, enriched);
    } catch (error) {
      console.error('[Analytics] Failed to track event:', event, error);
    }
  }

  /**
   * Scorecard charts key on snake_case (visit_id, org_id, crew_id, wasOffline).
   * Attach both spellings so existing dashboards fill without a rewrite (RUT-3490).
   */
  private withScorecardAliases(
    properties?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (!properties) return properties;
    const orgId = (properties.org_id ?? properties.orgId) as string | undefined;
    const crewId = (properties.crew_id ?? properties.crewId) as string | undefined;
    const visitId = (properties.visit_id ?? properties.visitId) as string | undefined;
    return {
      ...properties,
      ...(orgId ? { orgId, org_id: orgId } : {}),
      ...(crewId ? { crewId, crew_id: crewId } : {}),
      ...(visitId ? { visitId, visit_id: visitId } : {}),
    };
  }

  /**
   * Track a page view with optional properties
   */
  pageView(pageName: string, path?: string): void {
    if (!this.posthog) {
      this.pendingEvents.push({
        event: '$pageview',
        properties: { pageName, path },
      });
      return;
    }

    try {
      this.posthog.capture('$pageview', { pageName, path });
      console.log('[Analytics] Page view:', pageName, path);
    } catch (error) {
      console.error('[Analytics] Failed to track page view:', pageName, error);
    }
  }

  /**
   * Identify the current user with traits.
   * Called by usePostHogIdentify hook after authentication.
   */
  identify(userId: string, traits?: UserTraits): void {
    if (!this.posthog) {
      console.log('[Analytics] Cannot identify - not ready');
      return;
    }

    try {
      this.posthog.identify(userId, traits);
      console.log('[Analytics] User identified:', userId);
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error);
    }
  }

  /**
   * Register super properties to include with every event.
   * Called by usePostHogIdentify hook after user is identified.
   */
  registerSuperProperties(properties: Record<string, unknown>): void {
    if (!this.posthog) {
      console.log('[Analytics] Cannot register super props - not ready');
      return;
    }

    try {
      this.posthog.register(properties);
      console.log('[Analytics] Super properties registered:', properties);
    } catch (error) {
      console.error('[Analytics] Failed to register super props:', error);
    }
  }

  /**
   * Unregister specific super properties.
   */
  unregisterSuperProperty(key: string): void {
    if (!this.posthog) return;

    try {
      this.posthog.unregister(key);
    } catch (error) {
      console.error('[Analytics] Failed to unregister super prop:', key, error);
    }
  }

  /**
   * Reset the current user identity.
   * Call on logout to ensure clean state for next user.
   */
  reset(): void {
    if (!this.posthog) return;

    try {
      // Clear super properties before reset
      this.posthog.unregister('orgId');
      this.posthog.unregister('org_id');
      this.posthog.unregister('userRole');
      this.posthog.unregister('role');
      this.posthog.reset();
      this.registerSuperProperties(appSuperProperties);
      console.log('[Analytics] User reset');
    } catch (error) {
      console.error('[Analytics] Failed to reset:', error);
    }
  }

  /**
   * Flush any pending events that were queued before PostHog was ready
   */
  private flushPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;

    console.log(
      '[Analytics] Flushing',
      this.pendingEvents.length,
      'pending events'
    );

    for (const { event, properties } of this.pendingEvents) {
      this.posthog?.capture(event, properties);
    }

    this.pendingEvents = [];
  }

  /**
   * Check if analytics is enabled and ready
   */
  isReady(): boolean {
    return this.posthog !== null;
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();
